import { type FC, useContext, useEffect, useRef, useState } from "react";

import { MonitoringEventTypeType } from "../../../../shared/types/monitoring";
import { PageBanners } from "../banners/PageBanners";

import { ErrorScreen } from "components/LoadingScreen";
import { SUBMISSION_ID_QUERY_PARAM, WAITED_TOO_LONG_TIMEOUT_MS } from "components/constants";
import { PageWorksheetHeader } from "components/materials/page/content/PageWorksheetHeader";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { useWakeLock } from "mds/hooks/use-wake-lock";
import { LoadingDotsIcon } from "mds/icons";
import { DocViewContext, type PageTabVariant } from "providers/DocViewProvider";
import { docCache, forceRefreshDoc, useWorksheet } from "sharedb/ShareDBDoc";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectIsTimeline, submissionSelectors } from "store/selectors";
import { aiStateActions } from "store/slices/ai";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { rollbarAndLogError } from "utils/logger";
import { usePresentation } from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";
import { TimelineWorksheet } from "worksheets/views/TimelineWorksheet";
import { WorksheetFromShareDBDoc } from "worksheets/views/Worksheet";

type PageWorksheetProps = {
  page: PageType;
  selectedTabVariant?: PageTabVariant;
  selectedAccessId?: string | null;
};

export const PageWorksheet: FC<PageWorksheetProps> = ({
  page,
  selectedTabVariant,
  selectedAccessId,
}) => {
  const { getQueryParam, deleteQueryParams } = useQueryParamsManager();
  const { isProjecting, isFeaturing, accessId } = useContext(DocViewContext);
  const dispatch = useAppDispatch();
  const pageId = page.id;
  const isTimeline = useAppSelector(selectIsTimeline);
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => submissionSelectors.selectById(s, submissionId));
  const docId = page.worksheet_id;
  const { doc } = useWorksheet(docId);
  const { lock, release, error: wakeLockError } = useWakeLock();
  const { inPresentationMode, isFollowing, isPresentingUser } = usePresentation();

  const pageSubmission = submission?.page_id === pageId ? submission : null;

  const [initError, setInitError] = useState<string | null>(null);
  const initTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const isResponsesTab = selectedTabVariant === "responses";
  const docLoaded = Boolean(doc);

  if (doc && docLoaded && !isResponsesTab) {
    // This is a hack to make the doc available to the AI sidebar
    docCache.current = doc;
    // This is a hack to make the doc available globally for debugging purposes
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (window as any).doc = doc;
  }

  useEffect(() => {
    const effective = selectedAccessId ?? accessId ?? null;
    dispatch(aiStateActions.setWorksheetChatDocViewAccessId(effective));
    return () => {
      dispatch(aiStateActions.setWorksheetChatDocViewAccessId(null));
    };
  }, [selectedAccessId, accessId, dispatch]);

  useEffect(() => {
    if (!docLoaded) {
      initTimeout.current = setTimeout(() => setInitError("Timeout"), WAITED_TOO_LONG_TIMEOUT_MS);
    } else {
      if (initTimeout.current) {
        clearTimeout(initTimeout.current);
        initTimeout.current = undefined;
      }

      setInitError(null);
    }

    return () => clearTimeout(initTimeout.current);
  }, [docLoaded]);

  useEffect(() => {
    if (wakeLockError) {
      return;
    }
    if (inPresentationMode && (isPresentingUser || isFollowing)) {
      // Prevent screen from dimming while presenting, or following the presentation.
      lock();
    } else {
      // When presentation ends, or student stops following the presentation,
      // allow the screen to dim again.
      release();
    }
    return release as () => void;
  }, [inPresentationMode, isPresentingUser, isFollowing, lock, release, wakeLockError]);

  const createMonitoringEvent = useCreateMonitoringEvent();
  useEffect(() => {
    if (initError) {
      createMonitoringEvent(MonitoringEventTypeType.PAGE_WORKSHEET_LOAD_ERROR, {
        pageId,
        docId,
      });
    }
  }, [initError, createMonitoringEvent, pageId, docId]);

  useEffect(() => {
    if (submission?.id && !pageSubmission?.id) {
      rollbarAndLogError(
        `PageWorksheet: submission with id=${submission?.id} does not belong to page with id=${pageId}`,
      );

      // Chances are we have a submission query param that was wrongly added to this page, so let's just
      // remove it.
      deleteQueryParams([SUBMISSION_ID_QUERY_PARAM]);
    }
  }, [submission?.id, pageSubmission?.id, pageId, deleteQueryParams]);

  if (initError) {
    return (
      <div className="worksheet-page h-full">
        <div className="h-full">
          <ErrorScreen
            retryText={t("worksheet.error.try_again")}
            onRetry={() => {
              forceRefreshDoc(docId);
              setInitError(null);
            }}
          >
            <div className="body-m">{t("worksheet.error.loading_page")}</div>
          </ErrorScreen>
        </div>
      </div>
    );
  }

  // When switching to a new page, the doc might take some time to update, so we need to wait
  // until the loaded doc exists and matches the page's worksheet_id before rendering the page.
  if (!doc || !doc.data || doc?.id !== page.worksheet_id) {
    return (
      <div className="worksheet-page h-full">
        <div className="h-full">
          <div className="worksheet-page-content h-full">
            <div className="worksheet-column-wrapper mt-4">
              <div className="mb-2">{t("worksheet.loading.loading_page")}</div>
              {/* TODO: This should also be re-triable behind a timeout */}
              <LoadingDotsIcon />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isTimeline) {
    return <TimelineWorksheet docId={docId} />;
  }

  const showEmptySubmission =
    selectedTabVariant === "student_submission" &&
    selectedAccessId &&
    !pageSubmission?.worksheet_sharedb_version;

  return (
    <div className="worksheet-page flex flex-col">
      <div className="flex h-full items-start justify-center">
        <div className="worksheet-page-container flex w-full">
          <div className="worksheet-page-content flex h-full w-full flex-col items-center">
            {!isProjecting && !isFeaturing && (
              <>
                <PageWorksheetHeader
                  page={page}
                  selectedAccessId={selectedAccessId}
                  selectedTabVariant={selectedTabVariant}
                />

                <PageBanners doc={doc} page={page} selectedTabVariant={selectedTabVariant} />
              </>
            )}

            <article className="flex h-full w-full">
              <ErrorBoundary>
                <div className="relative w-full">
                  {showEmptySubmission ? (
                    <div />
                  ) : (
                    <WorksheetFromShareDBDoc
                      doc={doc}
                      selectedTabVariant={selectedTabVariant}
                      worksheetVersionNumber={pageSubmission?.worksheet_sharedb_version}
                    />
                  )}
                </div>
              </ErrorBoundary>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};
